import { getBackRoyalApiBaseConfig } from 'BackRoyalApi';
import { createApi } from 'ReduxHelpers';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { type AnyObject } from '@Types';
import transformKeyCase from 'Utils/transformKeyCase';
import {
    type RequiredProfilesForSendbirdBatchedResponse,
    type CareerProfile,
    type EditableCareerProfile,
    type EditableCareerProfileUpdate,
    type RecommendedClassmatesData,
    type RecommendedClassmatesResponse,
} from './CareerProfiles.types';

type EditableCareerProfileResponse = { contents: { careerProfiles: AnyObject[] } };

// The base query camelCases every response key. The editable view feeds forms whose paths come from
// the FormConfigs schemas, which are snake_cased, so this puts the keys back. It also restores the
// Google place_details keys, which are snake_cased at the source.
const toEditableCareerProfile = (response: EditableCareerProfileResponse) =>
    transformKeyCase<EditableCareerProfile>(response.contents.careerProfiles[0], { to: 'snakeCase' });

export const careerProfilesApi = createApi('careerProfilesApi', {
    ...getBackRoyalApiBaseConfig('careerProfilesApi'),
    tagTypes: ['EditableCareerProfile'],
    // oxlint-disable-next-line max-lines-per-function
    endpoints: builder => ({
        getEditableCareerProfile: builder.query<EditableCareerProfile, string>({
            query: careerProfileId => ({
                url: `career_profiles/${careerProfileId}.json`,
                params: { view: 'editable' },
            }),
            transformResponse: toEditableCareerProfile,
            providesTags: (_result, _error, careerProfileId) => [
                { type: 'EditableCareerProfile', id: careerProfileId },
            ],
        }),
        updateEditableCareerProfile: builder.mutation<EditableCareerProfile, EditableCareerProfileUpdate>({
            query: record => ({
                url: 'career_profiles.json',
                method: 'PUT',
                body: { record },
            }),
            transformResponse: toEditableCareerProfile,
            invalidatesTags: (_result, _error, record) => [{ type: 'EditableCareerProfile', id: record.id }],
        }),
        getRecommendedClassmates: builder.query<RecommendedClassmatesData[], string | null>({
            query: cohortId => ({
                url: 'career_profiles/recommended_classmates.json',
                params: { cohort_id: cohortId },
            }),
            transformResponse: (response: RecommendedClassmatesResponse) =>
                response.contents.careerProfiles?.map(({ name, avatarUrl, workExperiences, userId, ...rest }) => ({
                    name,
                    avatarUrl,
                    userId,
                    jobTitle: workExperiences?.[0]?.jobTitle,
                    company: workExperiences?.[0]?.professionalOrganization?.text,
                    workExperiences,
                    ...rest,
                })) || [],
            keepUnusedDataFor: 30 * 60,
        }),
        getProfileForSendbirdById: builder.query<CareerProfile, string>({
            query: userId => ({
                url: 'career_profiles.json',
                params: {
                    filters: {
                        userIds: [userId],
                        sendbirdUserIds: true,
                        only: true,
                    },
                    view: 'student_network_profiles',
                },
            }),
            transformResponse: (response: { contents: { careerProfiles: CareerProfile[] } }, _meta, userId) =>
                // If currentUser is an admin, it returns this user plus all of the other admins, so we need to find instead of using careerProfiles[0]
                response.contents.careerProfiles.find(cp => cp.userId === userId)!,
        }),
        getAllRequiredProfilesForSendbirdInBatches: builder.query<CareerProfile[], string[]>({
            queryFn: async (userIds, _api, _options, fetchWithBasQuery) => {
                const profiles: CareerProfile[] = [];
                let offset = 0;
                let hasMore = true;

                try {
                    while (hasMore) {
                        // We are awaiting in a loop to purposefully execute these requests
                        // synchronously to minimize potential server issues by
                        // creating so many ruby objects at one time
                        // oxlint-disable-next-line no-await-in-loop
                        const response = await fetchWithBasQuery({
                            url: 'career_profiles/index.json',
                            method: 'POST',
                            body: {
                                filters: { userIds, sendbirdUserIds: true, only: true },
                                offset,
                                limit: 25,
                                view: 'student_network_profiles',
                            },
                        });

                        if (response.error) throw response.error;

                        const { contents, meta } = response.data as RequiredProfilesForSendbirdBatchedResponse;
                        profiles.push(...(contents?.careerProfiles || []));
                        offset = profiles.length;
                        hasMore = offset < meta.totalCount;

                        if (hasMore) {
                            // oxlint-disable-next-line no-await-in-loop
                            await new Promise(r => {
                                setTimeout(r, 25);
                            });
                        }
                    }

                    return { data: profiles };
                } catch (error) {
                    return { error: error as FetchBaseQueryError };
                }
            },
        }),
        searchForSendbirdProfilesByUserName: builder.query<CareerProfile[], string>({
            query: nameText => ({
                url: 'career_profiles.json',
                params: {
                    filters: {
                        nameText,
                    },
                    limit: 20,
                    view: 'student_network_profiles',
                },
            }),
            transformResponse: (response: { contents: { careerProfiles: CareerProfile[] } }) =>
                response.contents?.careerProfiles || [],
            keepUnusedDataFor: 30 * 60,
        }),
    }),
});
